import React, { useMemo } from "react";
import { Cell, flexRender } from "@tanstack/react-table";
import clsx from "clsx";

import { TableMetaProps } from "@/hooks/table/useTableConfig";
import useClickOutside from "@/hooks/useClickOutside";
import type { UseEditableCellReturn } from "@/hooks/useEditableCell";
import Tooltip from "@/components/atoms/Tooltip";

type CellContentProps<T> = {
  cell: Cell<T, unknown>;
  editableCell: UseEditableCellReturn;
  index: number;
  onDetailClicked: () => void;
};

// FIXME The tooltip makes more problems than it solves for the first cell in combination with editing
// TODO cell.getValue and cell.column.columnDef.cell can be of type array, we need to handle "keys"
const CellContentInternal = <T,>({ cell, editableCell, index, onDetailClicked }: CellContentProps<T>) => {
  const { cellText, setCellText, inputRef, isEditing, containerRef, exitEditMode } = editableCell;

  useClickOutside(containerRef, () => {
    if (isEditing) {
      exitEditMode();
    }
  });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      // Save or exit based on value change
      exitEditMode();
    } else if (e.key === "Escape") {
      // Exit without saving
      exitEditMode(false);
    }
  };

  /** This is a custom cellContext that just removes some defaults to minimize the size of the passing object */
  const cellContext = {
    cell,
    column: cell.column,
    row: { original: cell.row.original, index: cell.row.index },
    getValue: () => cell.getValue(),
  };

  const renderedContent = useMemo(
    () => (
      <div
        className={clsx("w-full truncate", {
          "cursor-pointer": index === 0 && onDetailClicked,
        })}
        onClick={index === 0 && onDetailClicked ? () => onDetailClicked() : undefined}
      >
        {/* @ts-ignore */}
        {flexRender(cell.column.columnDef.cell, cellContext)}
      </div>
    ),
    [cell],
  );

  const meta: TableMetaProps = cell.column.columnDef.meta || {};
  const disableTooltip = meta?.disableTooltip || false;

  return (
    <div ref={containerRef} className="relative flex w-full">
      {isEditing ? (
        <input
          ref={inputRef}
          value={cellText}
          onChange={(e) => setCellText(e.target.value)}
          onKeyDown={handleKeyDown}
          className="w-full bg-transparent py-0.5 outline-none ring-0"
        />
      ) : !cell.getValue<unknown>() || disableTooltip ? (
        renderedContent
      ) : (
        <Tooltip position="bottom" popoverButton={renderedContent} className="w-full text-left">
          <p className="whitespace-normal">{String(cell.getValue<unknown>())}</p>
        </Tooltip>
      )}
    </div>
  );
};

const CellContent = React.memo(CellContentInternal) as typeof CellContentInternal;

export default CellContent;
